import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Checkbox,
  Chip,
  Tooltip,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  fetchSuppliers,
  vendorUpdate,
  deleteVendor,
  vendorCreate,
  ApproveSuplierapi,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";

// ✅ Contact Persons Section - COMPONENT KE BAHAR (no re-render issue)
const ContactPersonsSection = ({
  contactPersons = [],
  onAdd,
  onRemove,
  onChange,
}) => (
  <Box sx={{ mt: 2, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#555" }}
      >
        Contact Persons
      </Typography>
      <Tooltip title="Add New Contact Person" arrow>
        <IconButton
          size="small"
          onClick={onAdd}
          color="primary"
          sx={{
            border: "1px solid",
            borderColor: "primary.main",
            width: 28,
            height: 28,
            "&:hover": { bgcolor: "primary.main", color: "white" },
          }}
        >
          <AddIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Box>

    {contactPersons.map((person, idx) => (
      <Grid 
        container 
        spacing={1}  // Changed from 2 to 1 for better spacing
        key={idx} 
        sx={{ 
          mb: 1.5, 
          alignItems: "center",
          // Added background for better visibility
          bgcolor: idx % 2 === 0 ? "#fafafa" : "transparent",
          p: 1,
          borderRadius: 1,
        }} 
      >
        {/* Title/Prefix field */}
        <Grid item xs={12} sm={6} md={2}>
         <TextField
  select
  label="Title"
  fullWidth
  size="small"
  value={person.title || ""}
  onChange={(e) => onChange(idx, "title", e.target.value)}
  SelectProps={{
    MenuProps: {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      PaperProps: {
        style: {
          maxHeight: 200,
          zIndex: 9999,
        },
      },
    },
  }}
>
 
  <MenuItem value="Mr.">Mr.</MenuItem>
  <MenuItem value="Mrs.">Mrs.</MenuItem>
  <MenuItem value="Ms.">Ms.</MenuItem>
  <MenuItem value="Dr.">Dr.</MenuItem>
  <MenuItem value="Prof.">Prof.</MenuItem>
</TextField>
        </Grid>

        {/* Name field */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            autoComplete="off"
            value={person.contact_name || ""}
            onChange={(e) => onChange(idx, "contact_name", e.target.value)}
          />
        </Grid>

        {/* Contact No field */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Contact No"
            fullWidth
            size="small"
            autoComplete="off"
            value={person.contact_no || ""}
            onChange={(e) => {
              let value = e.target.value.replace(/[^0-9]/g, "");
              if (value.length > 10) value = value.slice(0, 10);
              onChange(idx, "contact_no", value);
            }}
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
          />
        </Grid>

        {/* Designation field */}
        <Grid item xs={10} sm={6} md={3}>
          <TextField
            label="Designation"
            fullWidth
            size="small"
            autoComplete="off"
            value={person.designation || ""}
            onChange={(e) => onChange(idx, "designation", e.target.value)}
          />
        </Grid>

        {/* Delete button */}
        <Grid item xs={2} sm={6} md={1} sx={{ textAlign: "center" }}>
          {contactPersons.length > 1 && (
            <Tooltip title="Remove" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => onRemove(idx)}
                sx={{
                  border: "1px solid #ffcdd2",
                  '&:hover': {
                    bgcolor: '#ffebee',
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    ))}
  </Box>
);
// ✅ MAIN COMPONENT
const VendorManagementTable = () => {
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState({ name: "" });

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewVendor, setViewVendor] = useState(null);

  const [editIndex, setEditIndex] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [vendorToApprove, setVendorToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);

  const [searchStatus, setSearchStatus] = useState("Pending");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [errors, setErrors] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedVendor, setExpandedVendor] = useState(null);

  // ✅ Empty contact person template
  const getEmptyContactPerson = () => ({
    title: "",
    contact_name: "",
    contact_no: "",
    designation: "",
  });

  const getEmptyFormData = () => ({
    name: "",
    email: "",
    address: "",
    contact: "",
    gst: "",
    pan: "",
    state: "",
    account_no: "",
    account_name: "",
    bank_name: "",
    branch_name: "",
    ifsc_code: "",
    contact_persons: [getEmptyContactPerson()],
    created_by: localStorage.getItem("user_id"),
  });
  const [formData, setFormData] = useState(getEmptyFormData());

  // ✅ Contact Person Handlers
  const handleAddContactPerson = () => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: [
        ...(prev.contact_persons || []),
        getEmptyContactPerson(),
      ],
    }));
  };

  const handleRemoveContactPerson = (index) => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: prev.contact_persons.filter((_, i) => i !== index),
    }));
  };

  const handleContactPersonChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.contact_persons || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, contact_persons: updated };
    });
  };

  useEffect(() => {
    fetchVendors(1, false);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVendors(1, false);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter.name, searchStatus]);

  const fetchVendors = async (pageNumber = 1, append = false) => {
    try {
      const res = await fetchSuppliers(pageNumber, {
        name: filter.name,
        status: searchStatus,
      });
      const newData = res.results || res || [];
      if (append) {
        setVendors((prev) => [...prev, ...newData]);
      } else {
        setVendors(newData);
      }
      setHasMore(!!res?.next);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMoreData = () => {
    if (hasMore) {
      setPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchVendors(nextPage, true);
        return nextPage;
      });
    }
  };

  const userRole = localStorage.getItem("role");

  const handleAddVendor = async () => {
    const requiredFields = ["name"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    const formatFields = ["email", "contact", "pan", "gst", "ifsc_code"];
    formatFields.forEach((field) => {
      const errorMsg = validateField(field, formData[field]);
      if (errorMsg) {
        newErrors[field] = errorMsg;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (nameExists(formData.name)) {
      const msg = "Creditor name already exists";
      toast.info(msg);
      setErrors((prev) => ({ ...prev, name: msg }));
      return;
    }

    try {
      const cleanedContactPersons = (formData.contact_persons || []).filter(
        (p) =>
          p.contact_name?.trim() ||
          p.contact_no?.trim() ||
          p.designation?.trim()
      );

      // ✅ DEBUG - Yahan dalo
      console.log("=== 🔍 DEBUG START ===");
      console.log("1. formData:", formData);
      console.log("2. formData.contact_persons:", formData.contact_persons);
      console.log("3. cleanedContactPersons:", cleanedContactPersons);

      const payload = {
        ...formData,
        name: formData.name.trim(),
        contact_persons: cleanedContactPersons,
      };

      console.log("4. FINAL PAYLOAD:", payload);
      console.log("5. JSON PAYLOAD:", JSON.stringify(payload, null, 2));
      console.log("=== 🔍 DEBUG END ===");

      await vendorCreate(payload);
      fetchVendors();
      resetForm();
      setOpenAddModal(false);
      toast.success("Creditor created successfully");
    } catch (error) {
      console.error("❌ Error:", error);
      console.error("❌ Response:", error?.response?.data);

      const serverMsg =
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.detail ||
        error?.message;
      if (serverMsg) {
        toast.info(serverMsg);
        setErrors((prev) => ({ ...prev, name: serverMsg }));
      } else {
        toast.error("Failed to create Creditor");
      }
    }
  };
  const handleUpdateVendor = async (id) => {
    const requiredFields = ["name"];
    let newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    const formatFields = ["email", "contact", "pan", "gst", "ifsc_code"];
    formatFields.forEach((field) => {
      const errorMsg = validateField(field, formData[field]);
      if (errorMsg) newErrors[field] = errorMsg;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!id) {
      toast.error("Creditor ID not found");
      return;
    }

    if (nameExists(formData.name, id)) {
      const msg = "Creditor name already exists";
      toast.info(msg);
      setErrors((prev) => ({ ...prev, name: msg }));
      return;
    }

    try {
      const cleanedContactPersons = (formData.contact_persons || []).filter(
        (p) =>
          p.contact_name?.trim() ||
          p.contact_no?.trim() ||
          p.designation?.trim()
      );

      const res = await vendorUpdate(id, {
        ...formData,
        name: formData.name.trim(),
        contact_persons: cleanedContactPersons,
      });
      if (res) {
        toast.success("Creditor updated successfully ✅");
        await fetchVendors();
        setOpenEditModal(false);
        setEditIndex(null);
        setFormData(getEmptyFormData());
        setErrors({});
      } else {
        toast.error("Failed to update Creditor");
      }
    } catch (error) {
      const serverMsg =
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.detail ||
        error?.message;
      if (serverMsg) {
        toast.info(serverMsg);
        setErrors((prev) => ({ ...prev, name: serverMsg }));
      } else {
        toast.error("Error updating Creditor");
      }
    }
  };

  const handleDelete = async (vendor) => {
    try {
      await deleteVendor(vendor.id);
      const updated = vendors.filter((v) => v.id !== vendor.id);
      setVendors(updated);
      toast.success("Creditor deleted successfully");
    } catch (error) {
      toast.error("Failed to delete Creditor");
    }
  };

  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const supplierId = vendorToApprove.suppliers_id || vendorToApprove.id;
      const updated = await ApproveSuplierapi(
        supplierId,
        approvedById,
        actionType,
        remarkInput
      );

      setVendors((prev) =>
        prev.map((item) =>
          (item.suppliers_id || item.id) === supplierId
            ? { ...item, ...updated }
            : item
        )
      );

      const msg =
        actionType === "Approved"
          ? "Supplier approved successfully!"
          : "Supplier rejected successfully!";
      toast.success(msg);

      setOpenApproveRejectModal(false);
      setOpenEditModal(false);
      setRemarkInput("");
      setActionType(null);
      setVendorToApprove(null);
    } catch (error) {
      toast.error("Failed to update supplier approval.");
    }
  };

  const handleViewVendor = (vendor) => {
    setViewVendor(vendor);
    setOpenViewModal(true);
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setErrors({});
  };

  const nameExists = (name, excludeId = null) => {
    const list = Array.isArray(vendors) ? vendors : [];
    const target = (name || "").trim().toLowerCase();
    return list.some(
      (v) =>
        (v?.name || "").trim().toLowerCase() === target &&
        (excludeId ? v.id !== excludeId : true)
    );
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "email":
        if (value && !/^\S+@\S+\.\S+$/.test(value))
          error = "Invalid email (e.g. name@example.com)";
        break;
      case "contact":
        if (value && !/^\d{10}$/.test(value))
          error = "Invalid phone number (e.g. 9876543210)";
        break;
      case "pan":
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value.toUpperCase()))
          error = "Invalid PAN (e.g. ABCDE1234F)";
        break;
      case "gst":
        if (
          value &&
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
            value.toUpperCase()
          )
        )
          error = "Invalid GST (e.g. 27ABCDE1234F1Z5)";
        break;
      case "ifsc_code":
        if (value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase()))
          error = "Invalid IFSC (e.g. ABCD0123456)";
        break;
      default:
        break;
    }
    return error;
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  function formatOnlyDate(dateTimeString, format = "dd-mm-yyyy") {
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    if (format === "dd-mm-yyyy") return `${day}-${month}-${year}`;
    if (format === "yyyy-mm-dd") return `${year}-${month}-${day}`;
    throw new Error("Unsupported format. Use 'dd-mm-yyyy' or 'yyyy-mm-dd'.");
  }

  // Mobile Card Component
  const MobileVendorCard = ({ vendor, index }) => {
    const isExpanded = expandedVendor === vendor.id;

    return (
      <Box key={vendor.id}>
        <Box
          onClick={() =>
            setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": { bgcolor: "#e3f2fd" },
            borderLeft: `4px solid ${vendor.status === "Approved"
                ? "#4caf50"
                : vendor.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
              }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
              >
                {vendor.name}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{vendor.contact}</span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {truncateText(vendor.email, 15)}
              </span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${vendor.status === "Approved"
                  ? "#4caf50"
                  : vendor.status === "Rejected"
                    ? "#f44336"
                    : "#ff9800"
                }`,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  STATUS
                </Typography>
                <Chip
                  label={vendor.status}
                  color={
                    vendor.status === "Approved"
                      ? "success"
                      : vendor.status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  GST NO
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {vendor.gst || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  PAN
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {vendor.pan || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>
                  ACCOUNT NO
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {vendor.account_no || "-"}
                </Typography>
              </Box>
            </Box>

            {vendor.address && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", mb: 0.3 }}
                >
                  ADDRESS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    lineHeight: 1.3,
                  }}
                >
                  {vendor.address}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      vendor.status === "Approved"
                        ? "#e8f5e9"
                        : vendor.status === "Rejected"
                          ? "#ffebee"
                          : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${vendor.status === "Approved"
                        ? "#4caf50"
                        : vendor.status === "Rejected"
                          ? "#f44336"
                          : "#ff9800"
                      }`,
                  }}
                >
                  <Checkbox
                    checked={
                      vendor.status === "Approved" ||
                      vendor.status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setFormData({
                        ...vendor,
                        contact_persons:
                          vendor.contact_persons?.length > 0
                            ? vendor.contact_persons
                            : [getEmptyContactPerson()],
                      });
                      setVendorToApprove(vendor);
                      setEditIndex(index);
                      setOpenEditModal(true);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.75)",
                      color:
                        vendor.status === "Approved"
                          ? "#4caf50"
                          : vendor.status === "Rejected"
                            ? "#f44336"
                            : "#ff9800",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        vendor.status === "Approved"
                          ? "#2e7d32"
                          : vendor.status === "Rejected"
                            ? "#c62828"
                            : "#f57c00",
                    }}
                  >
                    {vendor.status === "Approved"
                      ? "Approved"
                      : vendor.status === "Rejected"
                        ? "Rejected"
                        : "Approve"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.6 }}>
                <IconButton
                  size="small"
                  disabled={vendor.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData({
                      ...vendor,
                      contact_persons:
                        vendor.contact_persons?.length > 0
                          ? vendor.contact_persons
                          : [getEmptyContactPerson()],
                    });
                    setEditIndex(index);
                    setVendorToApprove(null);
                    setOpenEditModal(true);
                  }}
                  sx={{
                    bgcolor:
                      vendor.status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        vendor.status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                <IconButton
                  size="small"
                  disabled={vendor.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setVendorToDelete(vendor);
                  }}
                  sx={{
                    bgcolor:
                      vendor.status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        vendor.status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewVendor(vendor);
                  }}
                >
                  <VisibilityIcon
                    sx={{ fontSize: "1rem", color: "#1976d2" }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* FILTERS BAR */}
      <Box
        sx={{
          pt: 1,
          pb: 1,
          px: 1,
          gap: 1,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1100,
          borderBottom: "1px solid #e0e0e0",
          mb: 1,
          mt: { xs: 4, sm: 0 },
        }}
      >
        <TextField
          label="Search by name"
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          value={filter.name || ""}
          onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { height: 38, borderRadius: 1 },
            "& .MuiInputLabel-root": { fontSize: "0.8rem" },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddModal(true)}
          sx={{
            height: 38,
            minWidth: 120,
            display: "flex",
            alignItems: "center",
            fontSize: "0.75rem",
            px: 2,
            borderRadius: 1,
            whiteSpace: "nowrap",
          }}
          startIcon={<AddIcon fontSize="small" />}
        >
          Creditor
        </Button>
      </Box>

      {/* Infinite Scroll Content */}
      <InfiniteScroll
        dataLength={vendors.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          <Typography textAlign="center" p={2}>
            Loading...
          </Typography>
        }
        endMessage={
          <Typography textAlign="center" p={2}>
            No more creditors!
          </Typography>
        }
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {vendors.map((vendor, index) => (
              <MobileVendorCard
                key={vendor.id}
                vendor={vendor}
                index={index}
              />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "88vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 2000,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      zIndex: 999,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      zIndex: 999,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell>GST No</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone No</TableCell>
                  <TableCell>PAN</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Account No.</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>IFSC Code</TableCell>
                  <TableCell>Bank Name</TableCell>
                  <TableCell>Branch Name</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      zIndex: 999,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {vendors.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": { backgroundColor: "#e3f2fd !important" },
                      },
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : index % 2 === 0
                              ? "#fafafa"
                              : "#ffffff",
                        zIndex: 998,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : index % 2 === 0
                              ? "#fafafa"
                              : "#ffffff",
                        zIndex: 998,
                      }}
                    >
                      {row.name}
                    </TableCell>
                    <TableCell>{row.gst}</TableCell>
                    <TableCell>
                      <Tooltip title={row.email} arrow>
                        <span>{truncateText(row.email, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.contact}</TableCell>
                    <TableCell>{row.pan}</TableCell>
                    <TableCell>
                      <Tooltip title={row.address} arrow>
                        <span>{truncateText(row.address, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.account_no}</TableCell>
                    <TableCell>{row.account_name}</TableCell>
                    <TableCell>{row.ifsc_code}</TableCell>
                    <TableCell>{row.bank_name}</TableCell>
                    <TableCell>{row.branch_name}</TableCell>
                    <TableCell>{formatOnlyDate(row.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={row.remarks} arrow>
                        <span>{truncateText(row.remarks, 20)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : index % 2 === 0
                              ? "#fafafa"
                              : "#ffffff",
                        zIndex: 998,
                      }}
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => {
                          setFormData({
                            ...row,
                            contact_persons:
                              row.contact_persons?.length > 0
                                ? row.contact_persons
                                : [getEmptyContactPerson()],
                          });
                          setEditIndex(index);
                          setVendorToApprove(null);
                          setOpenEditModal(true);
                        }}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setVendorToDelete(row);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewVendor(row);
                        }}
                      >
                        <VisibilityIcon
                          sx={{ fontSize: "1rem", color: "#1976d2" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* ========================================== */}
      {/* ✅ ADD VENDOR DIALOG                       */}
      {/* ========================================== */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="md"
        fullWidth
        autoComplete="off"
      >
        <DialogContent>
          <Grid container spacing={2}>
            {/* Row 1: Name | Email | Phone No */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Name"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, name: value });
                  const errorMsg = validateField("name", value);
                  setErrors((prev) => ({ ...prev, name: errorMsg }));
                }}
                required
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  setFormData({ ...formData, email: value });
                  const errorMsg = validateField("email", value);
                  setErrors((prev) => ({ ...prev, email: errorMsg }));
                }}
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Phone No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.contact}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9]/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  setFormData({ ...formData, contact: value });
                  const errorMsg = validateField("contact", value);
                  setErrors((prev) => ({ ...prev, contact: errorMsg }));
                }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                required
                error={!!errors.contact}
                helperText={errors.contact}
              />
            </Grid>

            {/* Row 2: GST | PAN | Account No */}
            <Grid item xs={12} md={4}>
              <TextField
                label="GST No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.gst}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, gst: value });
                  const errorMsg = validateField("gst", value);
                  setErrors((prev) => ({ ...prev, gst: errorMsg }));
                }}
                inputProps={{ maxLength: 15 }}
                error={!!errors.gst}
                helperText={errors.gst}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="PAN No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.pan}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, pan: value });
                  const errorMsg = validateField("pan", value);
                  setErrors((prev) => ({ ...prev, pan: errorMsg }));
                }}
                inputProps={{ maxLength: 10 }}
                error={!!errors.pan}
                helperText={errors.pan}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Account No"
                margin="dense"
                autoComplete="off"
                value={formData.account_no}
                inputProps={{ maxLength: 18 }}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, account_no: value });
                }}
                required
                error={!!errors.account_no}
                helperText={errors.account_no}
              />
            </Grid>

            {/* Row 3: Account Name | Bank Name | Branch Name */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Account Name"
                margin="dense"
                autoComplete="off"
                value={formData.account_name}
                onChange={(e) =>
                  setFormData({ ...formData, account_name: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bank Name"
                margin="dense"
                autoComplete="off"
                value={formData.bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, bank_name: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Branch Name"
                margin="dense"
                autoComplete="off"
                value={formData.branch_name}
                onChange={(e) =>
                  setFormData({ ...formData, branch_name: e.target.value })
                }
                required
              />
            </Grid>

            {/* Row 4: IFSC Code */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="IFSC Code"
                margin="dense"
                autoComplete="off"
                value={formData.ifsc_code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, ifsc_code: value });
                  const errorMsg = validateField("ifsc_code", value);
                  setErrors((prev) => ({ ...prev, ifsc_code: errorMsg }));
                }}
                inputProps={{ maxLength: 11 }}
                required
                error={!!errors.ifsc_code}
                helperText={errors.ifsc_code}
              />
            </Grid>
          </Grid>

          {/* ✅ CONTACT PERSONS SECTION - Props pass karo */}
          <ContactPersonsSection
            contactPersons={formData.contact_persons}
            onAdd={handleAddContactPerson}
            onRemove={handleRemoveContactPerson}
            onChange={handleContactPersonChange}
          />

          {/* Address Field */}
          <Grid container sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Address"
                autoComplete="off"
                fullWidth
                margin="dense"
                multiline
                rows={1}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddModal(false);
              setFormData(getEmptyFormData());
              resetForm();
            }}
            color="error"
          >
            Close
          </Button>
          <Button onClick={handleAddVendor} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================== */}
      {/* VIEW VENDOR DIALOG                         */}
      {/* ========================================== */}
      <Dialog
        open={openViewModal}
        onClose={() => {
          setOpenViewModal(false);
          setViewVendor(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            Creditor Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {viewVendor && (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: "70vh", overflow: "auto", boxShadow: "none" }}
            >
              <Table size="small">
                <TableBody>
                  {[
                    { label: "Name", value: viewVendor.name },
                    { label: "Email", value: viewVendor.email },
                    { label: "Phone No", value: viewVendor.contact },
                    { label: "GST No", value: viewVendor.gst },
                    { label: "PAN", value: viewVendor.pan },
                    { label: "State", value: viewVendor.state },
                    {
                      label: "Address",
                      value: viewVendor.address,
                      wrap: true,
                    },
                    {
                      label: "Status",
                      value: (
                        <Chip
                          label={viewVendor.status || "Pending"}
                          color={
                            viewVendor.status === "Approved"
                              ? "success"
                              : viewVendor.status === "Rejected"
                                ? "error"
                                : "warning"
                          }
                          variant="outlined"
                          size="small"
                        />
                      ),
                    },
                    { label: "Account No.", value: viewVendor.account_no },
                    { label: "Account Name", value: viewVendor.account_name },
                    { label: "Bank Name", value: viewVendor.bank_name },
                    { label: "Branch Name", value: viewVendor.branch_name },
                    { label: "IFSC Code", value: viewVendor.ifsc_code },
                    {
                      label: "Created Date",
                      value: viewVendor.created_at
                        ? formatOnlyDate(viewVendor.created_at)
                        : "-",
                    },
                    {
                      label: "Approved By",
                      value: viewVendor.authorized_by_name,
                    },
                    {
                      label: "Remarks",
                      value: viewVendor.remarks,
                      wrap: true,
                    },
                  ].map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": { bgcolor: "#e3f2fd" },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "35%",
                          fontSize: "0.75rem",
                          borderRight: "1px solid #e0e0e0",
                          color: "#333",
                        }}
                      >
                        {item.label}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          whiteSpace: item.wrap ? "normal" : "nowrap",
                          wordBreak: item.wrap ? "break-word" : "normal",
                        }}
                      >
                        {item.value || "-"}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Contact Persons in View Modal */}
                  {viewVendor.contact_persons &&
                    viewVendor.contact_persons.length > 0 && (
                      <>
                        <TableRow sx={{ bgcolor: "#e3f2fd" }}>
                          <TableCell
                            colSpan={2}
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "#1976d2",
                            }}
                          >
                            Contact Persons
                          </TableCell>
                        </TableRow>
                        {viewVendor.contact_persons.map((cp, idx) => (
                          <TableRow key={idx}>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                                color: "#333",
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              Person {idx + 1}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>
                              <strong>
                                {cp.title ? `${cp.title} ${cp.contact_name}` : cp.contact_name || "-"}
                              </strong> |{" "}
                              {cp.contact_no || "-"} |{" "}
                              <em>{cp.designation || "-"}</em>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, bgcolor: "#f5f5f5" }}>
          <Button
            onClick={() => {
              setOpenViewModal(false);
              setViewVendor(null);
            }}
            color="primary"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.75rem" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================== */}
      {/* ✅ EDIT VENDOR DIALOG                      */}
      {/* ========================================== */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setVendorToApprove(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Grid container spacing={2} autoComplete="off">
            {/* Row 1: Name | Email | Phone No */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Name"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  setFormData({ ...formData, email: value });
                  const errorMsg = validateField("email", value);
                  setErrors((prev) => ({ ...prev, email: errorMsg }));
                }}
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Phone No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.contact}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9]/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  setFormData({ ...formData, contact: value });
                  const errorMsg = validateField("contact", value);
                  setErrors((prev) => ({ ...prev, contact: errorMsg }));
                }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                required
                error={!!errors.contact}
                helperText={errors.contact}
              />
            </Grid>

            {/* Row 2: GST | PAN | Account No */}
            <Grid item xs={12} md={4}>
              <TextField
                label="GST No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.gst}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, gst: value });
                  const errorMsg = validateField("gst", value);
                  setErrors((prev) => ({ ...prev, gst: errorMsg }));
                }}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="PAN No"
                fullWidth
                margin="dense"
                autoComplete="off"
                value={formData.pan}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, pan: value });
                  const errorMsg = validateField("pan", value);
                  setErrors((prev) => ({ ...prev, pan: errorMsg }));
                }}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Account No"
                margin="dense"
                autoComplete="off"
                value={formData.account_no}
                onChange={(e) =>
                  setFormData({ ...formData, account_no: e.target.value })
                }
              />
            </Grid>

            {/* Row 3: Account Name | Bank Name | Branch Name */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Account Name"
                margin="dense"
                autoComplete="off"
                value={formData.account_name}
                onChange={(e) =>
                  setFormData({ ...formData, account_name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bank Name"
                margin="dense"
                autoComplete="off"
                value={formData.bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, bank_name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Branch Name"
                margin="dense"
                autoComplete="off"
                value={formData.branch_name}
                onChange={(e) =>
                  setFormData({ ...formData, branch_name: e.target.value })
                }
              />
            </Grid>

            {/* Row 4: IFSC Code */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="IFSC Code"
                margin="dense"
                autoComplete="off"
                value={formData.ifsc_code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, ifsc_code: value });
                  const errorMsg = validateField("ifsc_code", value);
                  setErrors((prev) => ({ ...prev, ifsc_code: errorMsg }));
                }}
                inputProps={{ maxLength: 11 }}
                error={!!errors.ifsc_code}
                helperText={errors.ifsc_code}
              />
            </Grid>
          </Grid>

          {/* ✅ CONTACT PERSONS SECTION - Props pass karo */}
          <ContactPersonsSection
            contactPersons={formData.contact_persons}
            onAdd={handleAddContactPerson}
            onRemove={handleRemoveContactPerson}
            onChange={handleContactPersonChange}
          />

          {/* Address Field */}
          <Grid container sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                autoComplete="off"
                margin="dense"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          {vendorToApprove && userRole != "Staff" && (
            <>
              <Button
                color="success"
                size="small"
                onClick={() => {
                  setActionType("Approved");
                  setOpenApproveRejectModal(true);
                }}
              >
                Approve
              </Button>

              <Button
                sx={{ color: "orange" }}
                size="small"
                onClick={() => {
                  setActionType("Rejected");
                  setOpenApproveRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}

          <Button
            onClick={() => handleUpdateVendor(formData.id)}
            color="primary"
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              setFormData(getEmptyFormData());
              setVendorToApprove(null);
            }}
            color="error"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Modal */}
      <Dialog
        open={openApproveRejectModal}
        onClose={() => {
          setOpenApproveRejectModal(false);
          setRemarkInput("");
          setActionType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionType === "Approved" ? "Approve Supplier" : "Reject Supplier"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter a remark before{" "}
            {actionType === "Approved" ? "approving" : "rejecting"} this
            supplier:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            placeholder="Enter remark..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApproveRejectModal(false);
              setRemarkInput("");
              setActionType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color={actionType === "Approved" ? "success" : "error"}
            variant="contained"
            onClick={handleApproveReject}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setVendorToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 200 }}>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to delete{" "}
            <strong>{vendorToDelete?.name}</strong>?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
          >
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setVendorToDelete(null);
              }}
            >
              Close
            </Button>
            <Button
              size="small"
              color="error"
              onClick={async () => {
                await handleDelete(vendorToDelete);
                setAnchorEl(null);
                setVendorToDelete(null);
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default VendorManagementTable;